import Cookies from 'js-cookie'
import React, { useCallback } from 'react'
import { api } from '../api'
import { API_URL_REFRESH_TOKEN, ActionPermissions, AppActionPermissions, DISPLAY_PAGES } from '../constants'
import { logIn } from '../../layout/login/api/login.api'
import _, { isNumber } from 'lodash'
import history from '../history'
import { valueof } from '../../webapp-lib/pathspot-react/'
import { LoginResponse, UNAUTH_ALLOWED_PATHS, applyAmplitudeUserContext } from './auth'
import { classifyLoginError } from '../../layout/login/sso/sso'

const isAnyFieldDefinedOrTrue = <T extends Record<string, boolean | undefined>>(obj: T): boolean =>
  Object.values(obj).some((value) => value === true)

export type CurrentUser = {
  userId: number | null
  firstName: string
  lastName: string
  userEmail: string
}

export const LoginEvent = {
  login: `event-login-customer-facing-${process.env.NODE_ENV}`,
  logout: `event-logout-customer-facing-${process.env.NODE_ENV}`,
  loginCookie: `event-login-cookie-customer-facing-${process.env.NODE_ENV}`,
  loginToken: `event-login-token-customer-facing-${process.env.NODE_ENV}`,
} as const

type LoginEvent = keyof typeof LoginEvent

const defaultTokenExpiration = 900000
export enum LoginResponseStatus {
  success = 'success',
  fail = 'fail',
  error = 'error',
  unknown = 'unknown',
  ssoRequired = 'ssoRequired',
}

export const AuthenticationStatus = {
  loggedIn: `loginstatus:loggedIn:${process.env.NODE_ENV}`,
  loggedOut: `loginstatus:loggedOut:${process.env.NODE_ENV}`,
  unknown: `loginstatus:unknown:${process.env.NODE_ENV}`,
} as const

export type AuthenticationStatusType = valueof<typeof AuthenticationStatus>
export enum AuthContext {
  loggedIn = 'loggedIn',
  notLoggedIn = 'notLoggedIn',
  unknown = 'unknown',
}

const initialPermissionSet = {
  assign_tasks: false,
  assign_labels: false,
  assign_lg_labels :false,
  assign_lg_tasks: false,
}
export type LoginParams = {
  email: string
  password: string
}

export type AuthState = {
  currentContext: AuthContext
  currentUser: CurrentUser
  sidebarItems: Array<string>
  accessToken: string
  tokenExpiration: number
  developerOptions: boolean
  displayPages: DISPLAY_PAGES[] | Array<string>
  permissions: AppActionPermissions
  isAdmin?: boolean
  temperatureInC: boolean
  initialized: boolean
}
export const defaultCurrentUser: CurrentUser = {
  userId: null,
  userEmail: '',
  firstName: '',
  lastName: '',
}
export const defaultAuthState: AuthState = {
  currentContext: AuthContext.unknown,
  currentUser: { ...defaultCurrentUser },
  sidebarItems: [],
  accessToken: '',
  tokenExpiration: -1,
  developerOptions: false,
  displayPages: [],
  permissions: initialPermissionSet,
  isAdmin: false,
  initialized: false,
  temperatureInC: false,
}
export type AuthenticationProviderState = {
  authState: AuthState
  currentUser: CurrentUser
  isPathspotUser: boolean
  setAuthState: (authState: AuthState) => void
  userLogin: (values: LoginParams) => Promise<LoginResponseStatus>
  userLogout: () => void
  silentlyRefreshToken: () => Promise<LoginResponseStatus>
  checkPermissions: (permission: DISPLAY_PAGES) => boolean
  checkActionPermission: (permission: ActionPermissions) => boolean
}
const initialState: AuthenticationProviderState = {
  authState: { ...defaultAuthState },
  currentUser: { ...defaultAuthState.currentUser },
  isPathspotUser: false,
  setAuthState: () => {
    return null
  },
  userLogin: async () => LoginResponseStatus.unknown,
  userLogout: () => {
    return null
  },
  silentlyRefreshToken: async () => LoginResponseStatus.unknown,
  checkPermissions: () => false,
  checkActionPermission: () => false,
}
const AuthenticationContext = React.createContext(initialState)
export type AuthStateAndStatus = {
  authState?: AuthState
  success: boolean
}
type AuthenticationContextProps = {
  children: React.ReactNode
  initialAuthState: AuthStateAndStatus
}

const isCurrentUserPathspotUser = (email: string) => {
  return email.includes('@pathspot.com') || email.includes('@pathspottech.com')
}

function AuthenticationProvider({ children, initialAuthState }: AuthenticationContextProps) {
  const submitting = React.useRef<boolean | null>(null)
  const initialized = React.useRef<boolean>(false)
  const [authState, _setAuthState] = React.useState<AuthState>({ ...initialState.authState })

  const setAuthState = useCallback((newAuthState: AuthState) => {
    _setAuthState({ ...newAuthState })
  }, [])

  const updateAuthState = useCallback((updateData: Partial<AuthState>) => {
    _setAuthState((prevAuthState) => ({
      ...prevAuthState,
      ...updateData,
    }))
  }, [])

  const notLoggedInRedirect = useCallback(() => {
    const currentPath = window.location.pathname

    if (!UNAUTH_ALLOWED_PATHS.includes(currentPath)) {
      history.push('/login')
    }
  }, [])

  const setAuthStateFromLogout = useCallback(() => {
    if (authState.currentContext !== AuthContext.notLoggedIn) {
      updateAuthState({
        ...defaultAuthState,
        currentContext: AuthContext.notLoggedIn,
      } as AuthState)
    }
  }, [authState.currentContext, updateAuthState])

  const userLogout = useCallback(() => {
    Cookies.remove(LoginEvent.loginCookie)
    localStorage.removeItem(LoginEvent.logout)
    setAuthStateFromLogout()
    notLoggedInRedirect()
  }, [notLoggedInRedirect, setAuthStateFromLogout])

  const updatedAuthStateValidResponse = (logInResponse: LoginResponse) => {
    const {
      userId,
      userEmail,
      firstName,
      lastName,
      accessToken,
      accessTokenExpMs,
      sidebarItems,
      developerOptions,
      temperatureInC,
      permissions,
    } = logInResponse
    const currentUser: CurrentUser = { userId, userEmail, firstName, lastName }

    applyAmplitudeUserContext({ userId, userEmail, firstName, lastName })

    const responseAuthState: AuthState = {
      currentUser: currentUser,
      accessToken,
      currentContext: AuthContext.loggedIn,
      tokenExpiration: accessTokenExpMs || defaultTokenExpiration,
      sidebarItems,
      permissions,
      displayPages: developerOptions ? [...sidebarItems, DISPLAY_PAGES.ITEM_DEVELOPER_OPTIONS] : [...sidebarItems],
      developerOptions,
      temperatureInC,
      initialized: true,
    }
    updateAuthState(responseAuthState)
  }
  const parseLoginResponse = (loginResponse: LoginResponse) => {
    try {
      if (loginResponse.accessToken) {
        const { accessToken, accessTokenExpMs } = loginResponse
        api.setToken(accessToken)
        Cookies.set(LoginEvent.loginCookie, AuthenticationStatus.loggedIn, {
          sameSite: 'None', // Allows cross-site cookies
          secure: true, // Required for cross-site cookies in Chrome
        })
        localStorage.setItem(LoginEvent.logout, AuthenticationStatus.loggedOut + Math.random())

        setTimeout(() => {
          silentlyRefreshToken()
        }, Math.floor(accessTokenExpMs / 2))

        updatedAuthStateValidResponse(loginResponse)
        return LoginResponseStatus.success
      }
      return LoginResponseStatus.fail
    } catch (e: unknown) {
      console.error('Error parsing login response: ', e)
      return LoginResponseStatus.error
    }
    return LoginResponseStatus.unknown
  }

  const silentlyRefreshToken = async () => {
    let loginResult: LoginResponseStatus = LoginResponseStatus.unknown

    submitting.current = true
    if (Cookies.get(LoginEvent.loginCookie) === AuthenticationStatus.loggedIn) {
      try {
        const silentLogInResponse: LoginResponse = await api
          .noAuth()
          .url(`${API_URL_REFRESH_TOKEN}`)
          .options({ credentials: 'include', mode: 'cors' })
          .post()
          .json()

        loginResult = parseLoginResponse(silentLogInResponse)
        submitting.current = false
      } catch (err) {
        submitting.current = false
        console.warn('!!!!! Attempt to refresh token caused an error !!!!!: ', _.cloneDeep({ loginResult, ...authState, err }))
      }
      if (loginResult === LoginResponseStatus.fail) {
        setAuthStateFromLogout()
      } else if (loginResult !== LoginResponseStatus.success) {
        console.error('Access token refresh attempt did not return a reliable result. Please contact PathSpot support.')
      }
    } else {
      notLoggedInRedirect()
    }

    return loginResult
  }

  const userLogin = async (values: LoginParams) => {
    const data = { email: values.email.toLowerCase(), password: values.password }
    let logInResponse: LoginResponse
    try {
      logInResponse = await logIn(data)
    } catch (err) {
      return classifyLoginError(err)
    }
    if (parseLoginResponse(logInResponse) === LoginResponseStatus.success) {
      updatedAuthStateValidResponse(logInResponse)
      return LoginResponseStatus.success
    }
    return LoginResponseStatus.fail
  }
  const checkPermissions = (page: DISPLAY_PAGES) => {
    return authState.displayPages.includes(page)
  }

  // returns boolean if permission exists and its corresponding value
  const checkActionPermission = (permission: ActionPermissions): boolean => {
    return isAnyFieldDefinedOrTrue(authState.permissions) && Object.hasOwn(authState.permissions, permission)
      ? authState.permissions[permission] ?? false
      : false
  }

  if (initialAuthState.success && initialized.current === false) {
    if (initialAuthState.authState) {
      const returnAuthState: LoginResponse = {
        ...initialAuthState.authState,
        ...initialAuthState.authState.currentUser,
        accessTokenExpMs: initialAuthState.authState.tokenExpiration,
      }
      updatedAuthStateValidResponse(returnAuthState)
      initialized.current = true
      setTimeout(() => {
        silentlyRefreshToken()
      }, Math.floor(initialAuthState.authState.tokenExpiration / 2))
    }
  }

  return (
    <AuthenticationContext.Provider
      value={{
        authState,
        currentUser: { ...authState.currentUser },
        isPathspotUser: isCurrentUserPathspotUser(authState.currentUser.userEmail),
        setAuthState,
        userLogin,
        userLogout,
        silentlyRefreshToken,
        checkPermissions,
        checkActionPermission,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  )
}

export { AuthenticationProvider, AuthenticationContext }